import { useCallback, useContext, useState, MouseEvent } from 'react'
import api from '../../api'
import { ProjectContext } from '../../contexts'
import { ProjectApiKey, projectRoles } from '../../types'
import Button from '../../ui/Button'
import RadioInput from '../../ui/form/RadioInput'
import TextInput from '../../ui/form/TextInput'
import FormWrapper from '../../ui/form/FormWrapper'
import Modal from '../../ui/Modal'
import { Table, useSearchTableState } from '../../components'
import { ArchiveIcon, CopyIcon, PlusIcon } from '../../ui/icons'
import Menu, { MenuItem } from '../../ui/Menu'
import { SingleSelect } from '../../ui/form/SingleSelect'
import { checkProjectRole, snakeToTitle } from '../../utils'
import { toast } from 'react-hot-toast/headless'
import Alert from '../../ui/Alert'
import Tag from '../../ui/Tag'
import { useTranslation } from 'react-i18next'

const TruncatedKey = ({ value }: { value?: string }) => (
    <Tag variant="plain" size="regular">{value ? `${value.slice(0, 5)}...${value.slice(-4)}` : '•••••'}</Tag>
)

export default function ProjectApiKeys() {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const state = useSearchTableState(useCallback(async params => await api.apiKeys.search(project.id, params), [project]))

    const [editing, setEditing] = useState<null | Partial<ProjectApiKey>>(null)
    const canManage = (key: ProjectApiKey) => checkProjectRole(key.role ?? 'support', project.role)
    const assignableRoles = projectRoles.filter(role => checkProjectRole(role, project.role))

    const handleArchive = async (id: number) => {
        if (confirm(t('delete_key_confirmation'))) {
            await api.apiKeys.delete(project.id, id)
            await state.reload()
        }
    }

    const handleCopy = async (event: MouseEvent<HTMLButtonElement>, value: string) => {
        event.preventDefault()
        event.stopPropagation()
        await navigator.clipboard.writeText(value)
        toast.success(t('copied_api_key'))
    }

    return (
        <>
            <Table
                {...state}
                columns={[
                    { key: 'name', title: t('name') },
                    {
                        key: 'scope',
                        title: t('scope'),
                        cell: ({ item }) => snakeToTitle(item.scope),
                    },
                    {
                        key: 'role',
                        title: t('role'),
                        cell: ({ item }) => item.scope === 'public'
                            ? undefined
                            : snakeToTitle(item.role ?? ''),
                    },
                    {
                        key: 'value',
                        title: t('value'),
                        cell: ({ item }) => (
                            <div className="cell-content">
                                <TruncatedKey value={item.value} />
                                {item.value && (
                                    <Button icon={<CopyIcon />} size="small" variant="plain" onClick={async (e) => await handleCopy(e, item.value!)} />
                                )}
                            </div>
                        ),
                    },
                    {
                        key: 'description',
                        title: t('description'),
                    },
                    {
                        key: 'options',
                        title: t('options'),
                        align: 'end',
                        cell: ({ item }) => canManage(item) && (
                            <Menu size="small">
                                <MenuItem icon={<ArchiveIcon />} onClick={async () => await handleArchive(item.id)}>
                                    {t('archive')}
                                </MenuItem>
                            </Menu>
                        ),
                    },
                ]}
                itemKey={({ item }) => item.id}
                onSelectRow={item => {
                    if (canManage(item)) setEditing(item)
                }}
                title={t('api_keys')}
                actions={
                    <Button
                        icon={<PlusIcon />}
                        size="small"
                        onClick={() => setEditing({ scope: 'public', role: 'support' })}
                    >
                        {t('create_key')}
                    </Button>
                }
            />
            <Modal
                title={editing?.id ? t('update_key') : t('create_key')}
                open={Boolean(editing)}
                onClose={() => setEditing(null)}
            >
                {editing?.value && <Alert variant="plain" title={t('key_value')}>{editing?.value}</Alert>}
                {
                    editing && (
                        <FormWrapper<ProjectApiKey>
                            onSubmit={
                                async ({ id, name, description, scope, role }) => {
                                    if (id) {
                                        await api.apiKeys.update(project.id, id, { name, description, role })
                                    } else {
                                        await api.apiKeys.create(project.id, { name, description, scope, role })
                                    }
                                    await state.reload()
                                    setEditing(null)
                                }
                            }
                            defaultValues={editing}
                            submitLabel={editing?.id ? t('update_key') : t('create_key')}
                        >
                            {
                                form => {
                                    const scope = form.watch('scope')
                                    return (
                                        <>
                                            <TextInput.Field
                                                form={form}
                                                name="name"
                                                label={t('name')}
                                                required
                                            />
                                            <TextInput.Field
                                                form={form}
                                                name="description"
                                                label={t('description')}
                                            />
                                            <RadioInput.Field
                                                form={form}
                                                name="scope"
                                                label={t('scope')}
                                                options={[
                                                    { key: 'public', label: t('public') },
                                                    { key: 'secret', label: t('scope_secret') },
                                                ]}
                                                disabled={!!editing?.id}
                                            />
                                            {
                                                scope === 'secret' && (
                                                    <SingleSelect.Field
                                                        form={form}
                                                        name="role"
                                                        label={t('role')}
                                                        options={assignableRoles}
                                                        getOptionDisplay={snakeToTitle}
                                                        required
                                                    />
                                                )
                                            }
                                        </>
                                    )
                                }
                            }
                        </FormWrapper>
                    )
                }
            </Modal>
        </>
    )
}
